/**
 * @param val - The number to format
 * @param showDecimal
 * @param placeholder - String to display if value can't be formatted (`NaN`, `undefined`, `null`, etc)
 */
export function formatCurrency(
  val: number | undefined | null,
  showDecimal = false,
  placeholder = '-',
): string {
  if (typeof val !== 'number' || !isFinite(val)) {
    return placeholder;
  }

  return val.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showDecimal ? 2 : 0,
    maximumFractionDigits: showDecimal ? 2 : 0,
  });
}

/**
 * @param val - The number to format, as a decimal (0.2 = 20%)
 * @param decimals - Number of decimal places
 * @param placeholder - String to display if value can't be formatted (`NaN`, `undefined`, `null`, etc)
 */
export function formatPercent(val: number | undefined | null, decimals = 0, placeholder = '-') {
  if (typeof val !== 'number' || !isFinite(val)) {
    return placeholder;
  }

  const num = val * 100;
  const mult = Math.pow(10, decimals);
  return (Math.round(num * mult) / mult).toFixed(decimals) + '%';
}

/**
 * Formats a number with comma thousands separator
 * @param val - The number to format
 * @param decimals - Number of decimal places
 * @param placeholder - String to display if value can't be formatted (`NaN`, `undefined`, `null`, etc)
 */
export function formatNumber(val: number | undefined | null, decimals = 0, placeholder = '-') {
  if (typeof val !== 'number' || !isFinite(val)) {
    return placeholder;
  }

  return val.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Takes a number of minutes and formats as HH:MM
 * @param minutes - Number of minutes
 * @param placeholder - String to display if value can't be formatted (`NaN`, `undefined`, `null`, etc)
 */
export function formatDuration(minutes: number | undefined | null, placeholder = '-') {
  if (typeof minutes !== 'number' || !isFinite(minutes)) {
    return placeholder;
  }

  return `${Math.floor(minutes / 60)}:${(minutes % 60).toString().padStart(2, '0')}`;
}

/**
 * Converts seconds to "Xm Ys" format (e.g. 1355 -> "22m 35s").
 * Omits minutes when 0 (e.g. 45 -> "45s"); omits seconds when 0 (e.g. 120 -> "2m").
 * Uses "Xh Ym Zs" format when >= 3600 seconds.
 * @param seconds - Number of seconds
 * @param placeholder - String to display if value can't be formatted (`NaN`, `undefined`, `null`, etc)
 */
export function formatSecondsToMinutesAndSeconds(
  seconds: number | undefined | null,
  placeholder = '-',
): string {
  if (typeof seconds !== 'number' || !isFinite(seconds) || seconds < 0) {
    return placeholder;
  }

  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    const parts = [`${hours}h`];
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0) parts.push(`${secs}s`);
    return parts.join(' ');
  }

  if (mins === 0) {
    return `${secs}s`;
  }
  if (secs === 0) {
    return `${mins}m`;
  }
  return `${mins}m ${secs}s`;
}

export function formatCountdown(remainingMs: number): string {
  if (remainingMs <= 0) return '0:00';
  const totalSeconds = Math.floor(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function formatMillisecondsToMinutes(
  milliseconds: number | undefined | null,
  placeholder = '-',
) {
  if (typeof milliseconds !== 'number' || !isFinite(milliseconds)) {
    return placeholder;
  }
  return Math.ceil(milliseconds / 1000 / 60);
}

export function formatMillisecondsToMinutesAndSeconds(
  milliseconds: number | undefined | null,
  placeholder = '-',
) {
  if (typeof milliseconds !== 'number' || !isFinite(milliseconds)) {
    return placeholder;
  }
  const minutes = Math.floor(milliseconds / 1000 / 60);
  const seconds = Math.ceil((milliseconds / 1000) % 60);
  if (minutes === 0) {
    return `${seconds}s`;
  }
  if (seconds === 0) {
    return `${minutes}m`;
  } else {
    return `${minutes}m ${seconds}s`;
  }
}

/**
 * Thousands abbreviation (1000 -> 1k)
 * @param val - The number to format
 * @param decimals - Number of decimal places
 * @param placeholder - String to display if value can't be formatted (`NaN`, `undefined`, `null`, etc)
 */
export function formatThousands(val: number | undefined | null, decimals = 0, placeholder = '-') {
  if (typeof val !== 'number' || !isFinite(val)) {
    return placeholder;
  } else if (Math.abs(val) < 1000) {
    return formatNumber(val);
  }

  return `${formatNumber(val / 1000, decimals)}K`;
}

export function toTitleCase(str: string) {
  return str.replace(/\w\w+/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
}

export function formatDate(date: string | undefined | null): string {
  if (!date) {
    return '-';
  }
  return date;
}

/**
 * Returns the ordinal suffix for a number (e.g. 1 -> "1st", 2 -> "2nd", 13 -> "13th")
 */
export function formatOrdinal(val: number): string {
  const mod100 = val % 100;
  if (mod100 >= 11 && mod100 <= 13) {
    return `${val}th`;
  }

  switch (val % 10) {
    case 1:
      return `${val}st`;
    case 2:
      return `${val}nd`;
    case 3:
      return `${val}rd`;
    default:
      return `${val}th`;
  }
}

export function formatNumberWithCommas(val: number | undefined | null, placeholder = '-') {
  if (typeof val !== 'number' || !isFinite(val)) {
    return placeholder;
  }
  return val.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
}
